import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import api from "@/lib/api";

const COLOURS = ["#A78BFA", "#4CC9F0", "#FF6B9D", "#FFD93D"];
const aud = (n) => `$${Number(n || 0).toLocaleString("en-AU", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export const SeasonCompareChart = () => {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("revenue");
  useEffect(() => { api.get("/admin/seasons/compare?years=3").then(({ data }) => setData(data)).catch(() => setData({ years: [], seasons: [] })); }, []);
  if (!data) return null;
  const rows = data.seasons.filter((s) => Object.keys(s.years).length).map((s) => {
    const r = { name: `${s.emoji} ${s.label}`, slug: s.slug };
    data.years.forEach((y) => { r[y] = s.years[y] ? s.years[y][metric] : null; r[`${y}_partial`] = s.years[y]?.partial; });
    return r;
  });
  const fmt = (v) => (metric === "revenue" ? aud(v) : v);
  return (
    <div className="bg-white rounded-3xl soft-shadow p-6 mb-4" data-testid="season-compare-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#A78BFA]" /><h3 className="font-display font-bold text-[#19324A]">Season sales, year over year</h3></div>
        <div className="flex gap-1 bg-[#FFF9EE] rounded-full p-1" data-testid="season-compare-metric">
          {[["revenue", "Revenue"], ["orders", "Orders"], ["units", "Items"]].map(([k, l]) => (
            <button key={k} onClick={() => setMetric(k)} className={`px-3 py-1 rounded-full text-xs font-bold ${metric === k ? "bg-[#19324A] text-white" : "text-[#19324A]/60"}`} data-testid={`season-compare-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#19324A]/60 mb-4">Paid orders containing each occasion's products during its window. * = season still running this year (figures so far).</p>
      {rows.length === 0 ? <p className="text-sm text-[#19324A]/50" data-testid="season-compare-empty">No seasonal sales yet.</p> : (
        <div className="h-72" data-testid="season-compare-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }} barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(25,50,74,.08)" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#19324A" }} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 11, fill: "#19324A" }} tickFormatter={fmt} width={64} />
              <Tooltip formatter={(v, n, p) => [fmt(v) + (p.payload[`${n}_partial`] ? " (so far)" : ""), n]} contentStyle={{ borderRadius: 16, border: "none", boxShadow: "0 8px 30px rgba(25,50,74,.12)", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {data.years.map((y, i) => <Bar key={y} dataKey={y} fill={COLOURS[i % COLOURS.length]} radius={[8, 8, 0, 0]} maxBarSize={38} />)}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full text-xs" data-testid="season-compare-table">
          <thead><tr className="text-left text-[#19324A]/50"><th className="py-1 pr-3">Occasion</th>{data.years.map((y) => <th key={y} className="py-1 pr-3 text-right">{y}</th>)}<th className="py-1 text-right">Change</th></tr></thead>
          <tbody>
            {data.seasons.filter((s) => Object.keys(s.years).length).map((s) => {
              const ys = data.years.filter((y) => s.years[y]);
              const last = ys.length >= 2 ? s.years[ys[ys.length - 2]][metric] : null, cur = ys.length ? s.years[ys[ys.length - 1]][metric] : null;
              const pct = last ? Math.round(((cur - last) / last) * 100) : null;
              return (
                <tr key={s.slug} className="border-t border-[#19324A]/8" data-testid={`season-compare-row-${s.slug}`}>
                  <td className="py-1.5 pr-3 font-bold text-[#19324A]">{s.emoji} {s.label}</td>
                  {data.years.map((y) => <td key={y} className="py-1.5 pr-3 text-right text-[#19324A]">{s.years[y] ? fmt(s.years[y][metric]) + (s.years[y].partial ? "*" : "") : "—"}</td>)}
                  <td className={`py-1.5 text-right font-bold ${pct == null ? "text-[#19324A]/40" : pct >= 0 ? "text-[#2E7D32]" : "text-[#B22234]"}`}>{pct == null ? "—" : `${pct >= 0 ? "▲" : "▼"} ${Math.abs(pct)}%`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-[10px] text-[#19324A]/45 mt-1">* season still running — figures so far</p>
      </div>
    </div>
  );
};
